import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { RefreshCw, ShoppingBag, Check, Zap, Globe, Crosshair, Plus, Trash2, X, Pencil, Save } from "lucide-react";
import { fetchPortfolio, listForSale, api } from "@/lib/api";
import Sidebar from "@/components/dashboard/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useBranding } from "@/lib/branding";
import { useTimezone, formatInTz, relativeUntil } from "@/lib/tz";

const REGISTRAR_LABEL = {
  transip: "TRANSIP",
  spaceship: "SPACESHIP",
  dynadot: "DYNADOT",
  hostingnl: "HOSTING.NL",
  manual: "MANUAL",
};
const REGISTRARS = ["transip", "spaceship", "dynadot", "hostingnl", "manual"];

export default function Portfolio() {
  const brand = useBranding();
  const { userTz, browserTz, mismatch } = useTimezone();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [price, setPrice] = useState("");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("all");
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const bulkTitleCase = async () => {
    const chosen = [...selected];
    if (chosen.length === 0) { toast.error("Select at least one domain"); return; }
    setBulkBusy(true);
    try {
      const { data: r } = await api.post("/portfolio/bulk-title-case", { domains: chosen });
      toast.success(`Applied CamelCase to ${r.count} domain(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Title-case failed");
    } finally {
      setBulkBusy(false);
    }
  };
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [whoisState, setWhoisState] = useState({ status: "idle", total: 0, done: 0, failed: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetchPortfolio();
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed");
    } finally {
      setLoading(false);
    }
  };
  const loadSyncStatus = async () => {
    try {
      const s = await api.get("/portfolio/sync/status").then((r) => r.data);
      setSyncStatus(s);
    } catch { /* silent */ }
  };
  const triggerSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/portfolio/sync").then((res) => res.data);
      const n = (r.created || []).length;
      toast.success(n ? `Auto-imported ${n} new domain(s)` : "No new domains to import");
      await Promise.all([load(), loadSyncStatus()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };
  useEffect(() => {
    load();
    loadSyncStatus();
    // Hydrate WHOIS refresh state so an in-flight job resumes polling
    (async () => {
      try {
        const { data } = await api.get("/portfolio/whois/status");
        if (data && data.status) setWhoisState(data);
      } catch { /* silent */ }
    })();
  }, []);

  const startWhoisRefresh = async () => {
    try {
      const { data } = await api.post("/portfolio/whois/refresh");
      if (data.started === false) {
        toast.info("WHOIS refresh already in progress");
      } else {
        toast.success("WHOIS refresh started");
      }
      setWhoisState(s => ({ ...s, status: "running" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start WHOIS refresh");
    }
  };

  const scheduleSnipeFromWhois = async (d) => {
    try {
      const { data } = await api.post("/snipes/from-portfolio", {
        domain: d.domain,
        registrar: d.registrar,
        offset_seconds: -5,   // arm 5s before the predicted drop
      });
      toast.success(`Snipe scheduled · ${d.domain} @ ${formatInTz(data.target_time, userTz, { second: "2-digit" })}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      toast.error(msg || "Could not auto-schedule — enter target time manually on Snipes page");
    }
  };

  // Poll WHOIS refresh status while running
  useEffect(() => {
    if (whoisState.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/portfolio/whois/status");
        setWhoisState(data);
        if (data.status === "done" || data.status === "failed") {
          clearInterval(t);
          if (data.status === "done") {
            toast.success(`WHOIS refresh done · ${data.done} ok · ${data.failed} failed`);
            load();
          } else {
            toast.error(data.error || "WHOIS refresh failed");
          }
        }
      } catch { /* silent */ }
    }, 3000);
    return () => clearInterval(t);
  }, [whoisState.status]);

  const counts = useMemo(() => {
    const c = { all: (data?.domains || []).length };
    for (const r of REGISTRARS) c[r] = 0;
    for (const d of data?.domains || []) c[d.registrar] = (c[d.registrar] || 0) + 1;
    return c;
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let base = data.domains;
    if (tab !== "all") base = base.filter((d) => d.registrar === tab);
    if (!filter) return base;
    const f = filter.toLowerCase();
    return base.filter((d) => d.domain.includes(f));
  }, [data, filter, tab]);

  const toggle = (domain) => {
    const s = new Set(selected);
    if (s.has(domain)) s.delete(domain);
    else s.add(domain);
    setSelected(s);
  };
  const toggleAll = () => {
    const eligible = filtered.filter((d) => !d.listed).map((d) => d.domain);
    if (eligible.every((d) => selected.has(d))) setSelected(new Set());
    else setSelected(new Set(eligible));
  };

  const submit = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one domain");
      return;
    }
    setBusy(true);
    try {
      // Pass the registrar for each selected domain so downstream flows
      // (URL forwarding, DNS) don't have to scan every registrar.
      const domainRows = (data?.domains || []).filter((d) => selected.has(d.domain));
      const payloadDomains = domainRows.map((d) => ({ domain: d.domain, registrar: d.registrar }));
      const r = await listForSale({
        domains: payloadDomains,
        price: price ? Number(price) : null,
      });
      toast.success(`Listed ${r.created.length} domain(s)${r.skipped.length ? ` · ${r.skipped.length} skipped` : ""}`);
      setSelected(new Set());
      setPrice("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const eligibleCount = filtered.filter((d) => !d.listed).length;
  const allSelected = eligibleCount > 0 && filtered.filter((d) => !d.listed).every((d) => selected.has(d.domain));

  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="portfolio-page">
      <Sidebar />
      <main className="flex-1 flex flex-col border-l border-[#eaecf0] min-w-0">
        <header className="h-14 border-b border-[#eaecf0] px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="overline">{brand.brand_name}</span>
            <span className="text-[#D4D4D8]">/</span>
            <span className="text-sm font-medium">Portfolio</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-[#A1A1AA] hidden md:inline" data-testid="last-sync-label">
              {syncStatus?.last_at
                ? `Auto-sync every 12h · last ${new Date(syncStatus.last_at).toLocaleString()}`
                : "Auto-sync every 12h · never"}
            </span>
            <span className="text-[10px] font-mono text-[#A1A1AA] hidden md:inline" data-testid="tz-label" title={`Browser TZ: ${browserTz}`}>
              TZ · {userTz}{mismatch ? ` (browser: ${browserTz})` : ""}
            </span>
            <Button
              variant="outline"
              onClick={triggerSync}
              disabled={syncing}
              data-testid="sync-now-btn"
              className="rounded-sm"
            >
              <Zap className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-pulse" : ""}`} strokeWidth={1.5} />
              {syncing ? "Syncing…" : "Sync now"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowManualAdd(true)}
              data-testid="add-manual-btn"
              className="rounded-sm"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
              Add manually
            </Button>
            <Button
              variant="outline"
              onClick={startWhoisRefresh}
              disabled={whoisState.status === "running"}
              data-testid="whois-refresh-btn"
              className="rounded-sm"
            >
              <Globe className={`w-3.5 h-3.5 mr-1.5 ${whoisState.status === "running" ? "animate-pulse" : ""}`} strokeWidth={1.5} />
              {whoisState.status === "running"
                ? `WHOIS ${whoisState.done}/${whoisState.total}`
                : "Refresh WHOIS"}
            </Button>
            <Button
              variant="outline"
              onClick={load}
              disabled={loading}
              data-testid="refresh-portfolio-btn"
              className="rounded-sm"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
              Refresh
            </Button>
          </div>
        </header>

        {/* Tabs — one per registrar */}
        <div className="border-b border-[#eaecf0] px-6 flex items-center gap-1 overflow-x-auto" data-testid="registrar-tabs">
          {[["all", "All", counts.all], ...REGISTRARS.map((r) => [r, REGISTRAR_LABEL[r], counts[r] || 0])].map(([id, label, n]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`tab-${id}`}
              className={`text-[11px] font-mono uppercase px-3 py-3 border-b-2 -mb-px ${
                tab === id
                  ? "border-[#03142f] text-[#03142f]"
                  : "border-transparent text-[#525252] hover:text-[#171717]"
              }`}
            >
              {label}
              <span className="ml-2 text-[10px] text-[#A1A1AA]">{n}</span>
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="border-b border-[#eaecf0] px-6 py-4 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="overline">Filter</Label>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search…"
              data-testid="portfolio-filter"
              className="rounded-sm font-mono text-sm mt-1"
            />
          </div>
          <div className="w-40">
            <Label className="overline">Price (€, optional)</Label>
            <Input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="blank = offer-only"
              data-testid="portfolio-price"
              className="rounded-sm font-mono text-sm mt-1"
            />
          </div>
          <Button
            onClick={submit}
            disabled={busy || selected.size === 0}
            data-testid="list-selected-btn"
            className="rounded-sm bg-[#03142f] hover:bg-[#0b274d] text-white"
          >
            <ShoppingBag className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            {busy ? "Listing…" : `List ${selected.size || ""} selected`}
          </Button>
          <Button
            onClick={bulkTitleCase}
            disabled={bulkBusy || selected.size === 0}
            variant="outline"
            data-testid="bulk-titlecase-btn"
            className="rounded-sm"
            title="Send selected domains through Claude Sonnet to smart-split compound words and apply CamelCase (quicksnipe.io → QuickSnipe.io)"
          >
            <Pencil className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            {bulkBusy ? "Title-casing…" : `Title-case ${selected.size || ""}`}
          </Button>
        </div>

        {data && data.errors && Object.keys(data.errors).length > 0 && (
          <div
            data-testid="registrar-errors-banner"
            className="border-b border-[#eaecf0] bg-[#FFF1F2] px-6 py-3 text-xs font-mono text-[#BE123C]"
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="overline text-[#BE123C]">Registrar connection errors — fix in /settings</div>
              <button
                onClick={async () => {
                  try {
                    const r = await api.get("/settings/whoami").then((res) => res.data);
                    const ip = r.outbound_ip || "unknown";
                    await navigator.clipboard.writeText(ip);
                    toast.success(`Outbound IP ${ip} copied — paste into Dynadot allowlist`);
                  } catch (e) {
                    toast.error("Could not resolve outbound IP");
                  }
                }}
                data-testid="show-outbound-ip"
                className="text-[10px] font-mono uppercase px-2 py-1 border border-[#BE123C] text-[#BE123C] hover:bg-[#BE123C] hover:text-white whitespace-nowrap"
              >
                Show outbound IP
              </button>
            </div>
            {Object.entries(data.errors).map(([reg, msg]) => (
              <div key={reg} data-testid={`registrar-error-${reg}`} className="truncate">
                <span className="font-semibold uppercase mr-2">{reg}</span>
                <span className="text-[#7F1D1D]">{msg}</span>
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="p-10 font-mono text-sm text-[#525252]">Loading…</div>
          )}
          {!loading && data && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
                  <th className="px-6 py-2.5 text-left w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      data-testid="select-all"
                    />
                  </th>
                  <th className="text-left px-4 py-2.5 overline">Domain</th>
                  <th className="text-left px-4 py-2.5 overline">Registrar</th>
                  <th className="text-left px-4 py-2.5 overline">Expires</th>
                  <th className="text-left px-4 py-2.5 overline">
                    Predicted Drop
                    <span className="ml-2 text-[9px] font-normal normal-case tracking-normal text-[#999]">({userTz})</span>
                  </th>
                  <th className="text-right px-6 py-2.5 overline">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center text-sm text-[#525252]">
                      No domains found. Configure a registrar in Settings first.
                    </td>
                  </tr>
                )}
                {filtered.map((d) => (
                  <tr
                    key={`${d.registrar}-${d.domain}`}
                    className={`border-b border-[#eaecf0] hover:bg-[#FAFAFA] ${d.listed ? "opacity-50" : ""}`}
                    data-testid={`portfolio-row-${d.domain}`}
                  >
                    <td className="px-6 py-3">
                      <Checkbox
                        disabled={d.listed}
                        checked={selected.has(d.domain)}
                        onCheckedChange={() => toggle(d.domain)}
                        data-testid={`select-${d.domain}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono">
                      <PortfolioDomainCell row={d} onSaved={load} />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm bg-[#F4F4F5] text-[#525252]">
                        {REGISTRAR_LABEL[d.registrar] || d.registrar}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#525252]" data-testid={`expiry-${d.domain}`}>
                      {d.whois?.expires ? (
                        <div className="flex items-center gap-2">
                          <span>{d.whois.expires.slice(0, 10)}</span>
                          {d.whois.days_until_expiration != null && (
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded-sm ${
                                d.whois.is_expired
                                  ? "bg-[#FEE2E2] text-[#991B1B]"
                                  : d.whois.is_expiring_soon
                                  ? "bg-[#FEF3C7] text-[#92400E]"
                                  : "bg-[#F4F4F5] text-[#525252]"
                              }`}
                              data-testid={`expiry-days-${d.domain}`}
                            >
                              {d.whois.is_expired
                                ? "expired"
                                : `${d.whois.days_until_expiration}d`}
                            </span>
                          )}
                        </div>
                      ) : (
                        d.expiration || "—"
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs" data-testid={`drop-${d.domain}`}>
                      {d.whois?.predicted_drop ? (
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col leading-tight">
                            <span className="text-[#101828]">
                              {formatInTz(d.whois.predicted_drop.drop_at_utc, userTz)}
                            </span>
                            <span className="text-[10px] text-[#999]">
                              {relativeUntil(d.whois.predicted_drop.drop_at_utc)}
                              <span
                                className={`ml-2 px-1 rounded-sm text-[9px] uppercase tracking-wider ${
                                  d.whois.predicted_drop.confidence === "high"
                                    ? "bg-[#ECFDF5] text-[#047857]"
                                    : d.whois.predicted_drop.confidence === "medium"
                                    ? "bg-[#FEF3C7] text-[#92400E]"
                                    : "bg-[#F4F4F5] text-[#525252]"
                                }`}
                                title={`Source: ${d.whois.predicted_drop.source} · cycle ${d.whois.predicted_drop.cycle_days}d`}
                              >
                                {d.whois.predicted_drop.confidence}
                              </span>
                            </span>
                          </div>
                          {!d.listed && (
                            <button
                              onClick={() => scheduleSnipeFromWhois(d)}
                              data-testid={`snipe-from-whois-${d.domain}`}
                              className="p-1.5 rounded-sm border border-[#0A0A0A] text-[#101828] hover:bg-[#0A0A0A] hover:text-white transition-colors"
                              title="Schedule a burst snipe at the predicted drop time"
                            >
                              <Crosshair className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-[#999] text-[10px] uppercase tracking-wider">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right">
                      {d.listed ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[#047857]">
                          <Check className="w-3 h-3" strokeWidth={2} />
                          LISTED
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono text-[#A1A1AA]">available</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {data && (
          <div className="border-t border-[#eaecf0] px-6 py-2 text-[10px] font-mono text-[#A1A1AA] flex justify-between">
            <span>{data.count} domain(s) in portfolio</span>
            <span>{selected.size} selected</span>
          </div>
        )}
        {showManualAdd && (
          <ManualAddModal
            onClose={() => setShowManualAdd(false)}
            onAdded={() => { setShowManualAdd(false); load(); }}
          />
        )}
      </main>
    </div>
  );
}

function ManualAddModal({ onClose, onAdded }) {
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);

  const canonical = displayName.trim().toLowerCase();
  const valid = canonical.length > 3 && canonical.includes(".");

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error("Enter a valid domain like CasperDomains.com"); return; }
    setSaving(true);
    try {
      await api.post("/portfolio/manual", {
        domain: displayName.trim(),
        display_name: displayName.trim(),
      });
      toast.success(`Added ${displayName.trim()} to portfolio`);
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add domain");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(3,20,47,0.5)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="manual-add-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-lg shadow-2xl p-6"
        style={{ borderRadius: 12 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Portfolio · manual entry</div>
            <h2 className="font-display text-xl mt-1">Add a domain manually</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-[#f4f4f5] rounded"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <Label className="text-[11px] font-mono uppercase tracking-wider text-[#525252]">
          Domain
        </Label>
        <p className="text-xs text-[#666] mt-1 mb-2">
          Type the domain in the casing you want it displayed everywhere — e.g. <strong>CasperDomains.com</strong>, <strong>PrimeAcquire.com</strong>. We store the canonical lowercase form internally, but every list, lander, and email will show what you type here.
        </p>
        <Input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. CasperDomains.com"
          data-testid="manual-domain-input"
          autoFocus
          className="font-mono"
        />
        {canonical && canonical !== displayName.trim() && (
          <div className="text-[10px] text-[#98a2b3] mt-1 font-mono">
            Stored as <span className="text-[#525252]">{canonical}</span> · displayed as <span className="text-[#101828]">{displayName.trim()}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!valid || saving}
            data-testid="manual-add-submit"
            className="rounded-sm bg-[#03142f] text-white hover:bg-[#001D6E]"
          >
            {saving ? "Adding…" : "Add to portfolio"}
          </Button>
        </div>
      </form>
    </div>
  );
}


function PortfolioDomainCell({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.display_name || row.domain);
  const [saving, setSaving] = useState(false);
  const display = row.display_name || row.domain;

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() !== row.domain.toLowerCase()) {
      toast.error(`Display name must be the same domain, just in your preferred casing. Expected ${row.domain} (any casing).`);
      return;
    }
    setSaving(true);
    try {
      await api.patch("/portfolio/display-name", {
        domain: row.domain,
        display_name: trimmed,
      });
      toast.success(`Renamed to ${trimmed}`);
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save display name");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirm(`Remove manual entry ${display}? This only removes it from the manual portfolio — nothing at the registrar changes.`)) return;
    try {
      await api.delete(`/portfolio/manual/${encodeURIComponent(row.domain)}`);
      toast.success(`Removed ${display}`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") { setEditing(false); setValue(display); }
          }}
          className="font-mono h-8 text-sm"
          data-testid={`edit-display-input-${row.domain}`}
        />
        <button
          onClick={save}
          disabled={saving}
          className="p-1.5 hover:bg-[#f4f4f5] rounded text-[#065f46]"
          aria-label="Save display name"
          data-testid={`save-display-${row.domain}`}
        >
          <Save className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
        <button
          onClick={() => { setEditing(false); setValue(display); }}
          className="p-1.5 hover:bg-[#f4f4f5] rounded text-[#98a2b3]"
          aria-label="Cancel"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 group">
      <span>{display}</span>
      <button
        onClick={() => setEditing(true)}
        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-[#f4f4f5] rounded transition"
        aria-label="Edit display name"
        data-testid={`edit-display-${row.domain}`}
        title="Edit display casing (only the visible name changes; the domain itself stays the same)"
      >
        <Pencil className="w-3 h-3 text-[#525252]" strokeWidth={2} />
      </button>
      {row.manual && (
        <button
          onClick={del}
          className="p-1 opacity-0 group-hover:opacity-100 hover:bg-[#fef2f2] rounded transition"
          aria-label="Remove manual entry"
          data-testid={`delete-manual-${row.domain}`}
          title="Remove this manually-added domain from the portfolio"
        >
          <Trash2 className="w-3 h-3 text-[#dc2626]" strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

